import  { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import ScriptGrid from '#app/components/script-grid.tsx'
import { prettyDateShort } from '#app/utils/dateFormat.js'
import { prisma } from '#app/utils/db.server.js'
import { truncateText } from '#app/utils/misc.tsx'

export const meta: MetaFunction<typeof loader> = ({data}) => {
    const serverDetails = data?.serverDetails;
    const title = `Scripts for ${data?.serverDetails.name} | UO Addicts`;
    const description = `Browse scripts for the Ultima Online server ${data?.serverDetails.name} on UO Addicts`;
    return [
    { title: title},
    { name: "description", content: description },
    { name: "keywords", content: `scripts,uo,ultima online,shard,server,${serverDetails?.name}`},
    { property: "og:image", content: `https://uoaddicts.com/images/logos/${serverDetails?.logo}`},
    { property: "og:image:alt", content: "Script details for Ultima Online on UO Addicts"},
    { property: "twitter:card", content: "summary"},
    { property: "twitter:creator", content: "@jaseowns"},
    { property: "twitter:site", content: "@jaseowns"},
    { property: "og:title", content: title},
    { property: "og:description", content: description},
]}

export async function loader({ request, params }: LoaderFunctionArgs) {
    const serverDetails = await prisma.server.findFirst({
        where: { slug: params.serverSlug || "" }
    });
    
    if (!serverDetails) {
        throw new Response('Not found', { status: 404 })
    }

    const scripts = await prisma.script.findMany({
        where: {
            server: serverDetails.name,
            display: true
        },
        orderBy: {
            updatedAt: 'desc'
        },
        select: {
            id: true,
            slug: true,
            title: true,
            description: true,
            server: true,
            category: true,
            updatedAt: true,
            creator: {
                select: {
                    username: true
                }
            },
            reactions: {
                select: {
                    type: true
                }
            }
        }
    });

    // Process reactions
    const scriptsWithReactions = scripts.map(script => {
        const reactionCounts = script.reactions.reduce((acc, reaction) => {
            acc[reaction.type] = (acc[reaction.type] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        const reactions = Object.entries(reactionCounts).map(([type, count]) => ({
            type,
            count
        }));

        return {
            ...script,
            reactions
        };
    });

    return json({
        serverDetails,
        scripts: scriptsWithReactions
    });
}

export default function ServerScriptsRoute() {
    const { serverDetails, scripts } = useLoaderData<typeof loader>();

    return (
        <div className="container mb-48 mt-6 flex flex-col items-center">
            <div className="flex flex-wrap items-center justify-center">
                <h1 className="text-center text-h2">{serverDetails.name} Scripts</h1>
            </div>
            <Link to="/scripts" className="text-sm text-muted-foreground hover:text-primary transition-colors underline hover:no-underline">View All Scripts</Link>
            <Link to="/script/new" className="text-sm text-muted-foreground hover:text-primary transition-colors underline hover:no-underline">Upload New Script</Link>

            <div className="container flex flex-col rounded-3xl bg-muted mt-4 pt-4 p-12">
                {scripts.length === 0 ? (
                    <p className="text-center text-muted-foreground">No scripts found for this server.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {scripts.map(script => (
                            <ScriptGrid {...script} key={script.id} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
} 

export function ErrorBoundary() {
	return 		<GeneralErrorBoundary
    statusHandlers={{
        404: ({ params }) => (
            <div className="text-center">
                <p className="m-4">Cannot find a server related to: <br />"{params.serverSlug}"<br /> reach out if you think it should be added.</p>
                <Link to="/scripts/servers" className="text-xl underline hover:no-underline">&lt; Back to all servers</Link>
            </div>					
        ),
    }}
/>
}
    